import { useState, useMemo } from 'react';
import { Search, ChevronDown, HelpCircle, Mail, LifeBuoy } from 'lucide-react';
import { FAQ, SOPORTE_MAIL } from '../data/faq';

function normalize(str) {
  return str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// ── Ítem de pregunta (acordeón) ───────────────────────────────────────────
function FaqItem({ pregunta, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  return (
    <div className={`border-b border-slate-100 dark:border-slate-700 last:border-0 transition-colors ${open ? '' : 'hover:bg-slate-50/50 dark:hover:bg-slate-800/30'}`}>
      <button
        className="w-full flex items-start justify-between gap-4 py-4 px-5 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <span className={`text-sm font-semibold leading-snug ${open ? 'text-primary-600 dark:text-primary-400' : 'text-slate-700 dark:text-slate-300'}`}>
          {pregunta.q}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 mt-0.5 transition-transform duration-200 ${open ? 'rotate-180 text-primary-500' : 'text-slate-400'}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-4">
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-line">
            {pregunta.a}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Sin resultados ─────────────────────────────────────────────────────────
function SinResultados({ query }) {
  return (
    <div className="card flex flex-col items-center justify-center py-16 text-center max-w-lg mx-auto">
      <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
        <HelpCircle size={26} className="text-slate-400" />
      </div>
      <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">
        No encontramos resultados para "{query}"
      </h3>
      <p className="text-sm text-slate-400 dark:text-slate-500 mb-6">
        Intentá con otras palabras o consultá directamente con soporte.
      </p>
      <a
        href={`mailto:${SOPORTE_MAIL}?subject=Consulta%20Arecore%20ERP&body=Hola%2C%20no%20encontré%20respuesta%20para%20mi%20pregunta%20sobre%3A%20${encodeURIComponent(query)}`}
        className="btn-primary"
      >
        <Mail size={15} />
        Escribir a soporte
      </a>
      <p className="text-xs text-slate-400 mt-3">{SOPORTE_MAIL}</p>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function Ayuda() {
  const [search,   setSearch]   = useState('');
  const [catActiva, setCatActiva] = useState('');

  const categorias = FAQ.map(g => g.categoria);

  const resultados = useMemo(() => {
    const q = normalize(search.trim());

    return FAQ.flatMap(grupo => {
      if (catActiva && grupo.categoria !== catActiva) return [];

      const preguntas = q
        ? grupo.preguntas.filter(p =>
            normalize(p.q).includes(q) || normalize(p.a).includes(q)
          )
        : grupo.preguntas;

      if (preguntas.length === 0) return [];
      return [{ categoria: grupo.categoria, preguntas }];
    });
  }, [search, catActiva]);

  const totalPreguntas = resultados.reduce((acc, g) => acc + g.preguntas.length, 0);
  const buscando       = search.trim().length > 0;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Centro de ayuda</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Respuestas a las preguntas más frecuentes sobre Arecore ERP
          </p>
        </div>
      </div>

      {/* Buscador */}
      <div className="relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="input pl-11 py-3 text-sm w-full"
          placeholder="Buscá una pregunta... (ej: cómo anulo una venta)"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCatActiva(''); }}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors text-xs font-mono px-1"
          >
            ✕
          </button>
        )}
      </div>

      {/* Pills de categoría (solo sin búsqueda activa) */}
      {!buscando && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCatActiva('')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all
              ${!catActiva
                ? 'bg-primary-500 text-white'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
          >
            Todas
          </button>
          {categorias.map(cat => (
            <button
              key={cat}
              onClick={() => setCatActiva(cat === catActiva ? '' : cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all
                ${catActiva === cat
                  ? 'bg-primary-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Contador de resultados al buscar */}
      {buscando && totalPreguntas > 0 && (
        <p className="text-xs text-slate-400">
          {totalPreguntas} resultado{totalPreguntas !== 1 ? 's' : ''} para "{search}"
        </p>
      )}

      {/* Resultados */}
      {totalPreguntas === 0 ? (
        <SinResultados query={search} />
      ) : (
        <div className="space-y-4">
          {resultados.map(grupo => (
            <div key={grupo.categoria} className="card !p-0 overflow-hidden">
              {/* Header de categoría */}
              <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {grupo.categoria}
                </span>
                <span className="badge-neutral text-[10px]">{grupo.preguntas.length}</span>
              </div>

              {/* Preguntas */}
              {grupo.preguntas.map((p, i) => (
                <FaqItem
                  key={i}
                  pregunta={p}
                  defaultOpen={buscando}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Footer de soporte */}
      {totalPreguntas > 0 && (
        <div className="card bg-slate-50 dark:bg-slate-800/50 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
          <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center shrink-0">
            <LifeBuoy size={18} className="text-primary-500 dark:text-primary-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              ¿No encontraste lo que buscabas?
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              Escribinos a <a href={`mailto:${SOPORTE_MAIL}`} className="text-primary-500 hover:underline font-medium">{SOPORTE_MAIL}</a> y te respondemos a la brevedad.
            </p>
          </div>
          <a
            href={`mailto:${SOPORTE_MAIL}?subject=Consulta%20Arecore%20ERP`}
            className="btn-secondary text-sm shrink-0"
          >
            <Mail size={14} />
            Contactar
          </a>
        </div>
      )}
    </div>
  );
}
